import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import clsx from 'clsx';

import { buildCliDocsUrl, buildCliInternalPath, parseCliPageInfo } from '@/utils/cliDocsEnv';

import { getUrlSyncScript } from './UrlSyncScript';
import css from './index.module.less';

export default function CliDocs() {
  const navigate = useNavigate();
  const location = useLocation();

  const getPageFromPath = (pathname: string): string | undefined => {
    const match = pathname.match(/^\/cli-docs(?:\/(.+))?$/);
    return match ? match[1] : undefined;
  };

  const actualPage = getPageFromPath(location.pathname);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeUrl, setIframeUrl] = useState(() => buildCliDocsUrl(actualPage, location.hash));
  const [isLoading, setIsLoading] = useState(true);

  const [isInternalNavigation, setIsInternalNavigation] = useState(false);
  const [loadedUrls, setLoadedUrls] = useState(new Set<string>());
  const urlUpdateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const isFromIframe = (location.state as { fromIframe?: boolean })?.fromIframe;
    if (isFromIframe) {
      return;
    }
    const targetUrl = buildCliDocsUrl(actualPage, location.hash);
    setIframeUrl(targetUrl);
    setIsLoading(true);
  }, [actualPage, location.hash, location.state]);

  useEffect(() => {
    const isFromIframe = (location.state as { fromIframe?: boolean })?.fromIframe;

    if (!isFromIframe && !isInternalNavigation) {
      const targetUrl = buildCliDocsUrl(actualPage, location.hash);

      if (loadedUrls.has(targetUrl)) {
        setIframeUrl(targetUrl);
        setIsLoading(false);
      } else {
        if (iframeUrl !== targetUrl) {
          setIframeUrl(targetUrl);
          setIsLoading(true);
        } else {
          setIframeUrl(targetUrl);
          if (iframeRef.current && iframeRef.current.contentDocument) {
            setIsLoading(false);
          }
        }
      }
    } else if (isInternalNavigation) {
      setIsInternalNavigation(false);
    }
  }, [actualPage, location.hash, isInternalNavigation, location.state, iframeUrl, loadedUrls]);

  const updateBrowserUrl = useCallback(
    (iframeUrl: string) => {
      if (urlUpdateTimeoutRef.current) {
        clearTimeout(urlUpdateTimeoutRef.current);
      }

      urlUpdateTimeoutRef.current = setTimeout(() => {
        const { page, hash } = parseCliPageInfo(iframeUrl);
        if (page) {
          const newPath = buildCliInternalPath(page, hash);

          const currentFullPath = location.pathname + location.hash;
          if (currentFullPath !== newPath) {
            setIsInternalNavigation(true);
            navigate(newPath, {
              replace: true,
              state: { fromIframe: true },
            });
          }
        }
      }, 300);
    },
    [location.pathname, location.hash, navigate],
  );

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data && event.data.type === 'iframe-url-change') {
        const { url, timestamp } = event.data;

        const now = Date.now();
        if (timestamp && now - timestamp > 5000) {
          return;
        }

        updateBrowserUrl(url);
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [updateBrowserUrl]);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe || !iframeUrl) return;

    let urlCheckInterval: ReturnType<typeof setInterval>;

    const handleLoad = () => {
      setIsLoading(false);

      if (iframeUrl) {
        setLoadedUrls((prev) => new Set(prev).add(iframeUrl));
      }

      setTimeout(() => {
        try {
          const iframeWindow = iframe.contentWindow;
          const iframeDoc = iframe.contentDocument;

          if (iframeWindow && iframeDoc) {
            const script = iframeDoc.createElement('script');
            script.textContent = getUrlSyncScript(false);

            if (iframeDoc.readyState === 'loading') {
              iframeDoc.addEventListener('DOMContentLoaded', () => {
                iframeDoc.head.appendChild(script);
              });
            } else {
              iframeDoc.head.appendChild(script);
            }

            urlCheckInterval = setInterval(() => {
              try {
                const currentUrl = iframeWindow.location.href;
                updateBrowserUrl(currentUrl);
              } catch (e) {
                clearInterval(urlCheckInterval);
              }
            }, 500);
          }
        } catch (e) {
          // Ignore cross-origin errors
        }
      }, 100);
    };

    const handleError = () => {
      setIsLoading(false);
    };

    iframe.removeEventListener('load', handleLoad);
    iframe.removeEventListener('error', handleError);

    iframe.addEventListener('load', handleLoad);
    iframe.addEventListener('error', handleError);

    return () => {
      iframe.removeEventListener('load', handleLoad);
      iframe.removeEventListener('error', handleError);
      if (urlCheckInterval) {
        clearInterval(urlCheckInterval);
      }
      if (urlUpdateTimeoutRef.current) {
        clearTimeout(urlUpdateTimeoutRef.current);
      }
    };
  }, [iframeUrl, updateBrowserUrl]);

  useEffect(() => {
    if (location.hash) {
      window.scrollTo(0, 0);

      const preventHashScroll = (e: Event) => {
        e.preventDefault();
      };

      window.addEventListener('hashchange', preventHashScroll);

      return () => {
        window.removeEventListener('hashchange', preventHashScroll);
      };
    }
  }, [location.hash]);

  return (
    <div className="relative h-screen w-full">
      <div className={clsx(css['cli-nav-bar'])}>
        <div className={clsx(css['cli-nav-bar__fixed'])}>
          <div className={clsx(css['cli-nav-bar-container'])}>
            <div className={clsx(css['logo'])} onClick={() => navigate('/')}></div>
          </div>
        </div>
      </div>

      <div className="size-full" style={{ paddingTop: '82px' }}>
        {isLoading && !isInternalNavigation && (
          <div className="absolute inset-0 z-10 flex items-center justify-center"></div>
        )}

        {iframeUrl && (
          <iframe
            ref={iframeRef}
            src={iframeUrl}
            className="size-full border-0 bg-black"
            title="CLI 文档"
            allow="navigation"
            onLoad={() => {
              setIsLoading(false);
              if (iframeUrl) {
                setLoadedUrls((prev) => new Set(prev).add(iframeUrl));
              }
            }}
            onError={() => setIsLoading(false)}
          />
        )}
      </div>
    </div>
  );
}
